import { Box, Stack } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs, { Dayjs } from "dayjs"
import { ReactElement } from "react"
import { Control, Controller, UseFormSetError } from "react-hook-form"

import { useScreenSize } from "../../../hooks/useScreenSize"
import { TEXT_FIELD_DEFAULT_PROPS } from "../../../themes/baseTheme"
import { INPUT_HELP_TOOLTIP_SPACING } from "../../../themes/defaultThemeParams"
import {
    apiDateTimeToDayJs,
    dayJsToStrForDnoApi,
} from "../../../utils/general/generalUtils"
import HelpIcon from "../../helper/helpIcon"

interface IValidatedDatePickerProps {
    control: Control
    registerName: string
    label: string
    required?: boolean
    tooltip?: string | null
    disabled?: boolean
    setError: UseFormSetError<{
        [x: string]: object | undefined
    }>
    disableFutureDates?: boolean
}

/**
 * DatePicker with designed to be controlled by react-hook-form. Must be used
 * in a component with a useForm hook from react-hook-form.
 */
const ValidatedDatePicker = ({
    control,
    registerName,
    label,
    required,
    setError,
    tooltip = null,
    disabled = false,
    disableFutureDates = false,
}: IValidatedDatePickerProps): ReactElement => {
    const requiredRule = {
        required: { value: true, message: "This field is required" },
    }
    const rules = required ? { ...requiredRule } : {}
    const { isMob } = useScreenSize()
    return (
        <Box display={isMob ? undefined : "inline-flex"}>
            <Stack direction="row" spacing={INPUT_HELP_TOOLTIP_SPACING}>
                <Controller
                    name={registerName}
                    control={control}
                    defaultValue={null}
                    rules={rules}
                    render={({ field, fieldState: { error } }) => (
                        <DatePicker
                            value={apiDateTimeToDayJs(field.value)}
                            disabled={disabled}
                            onChange={(newValue: Dayjs | null, context) => {
                                if (!context.validationError) {
                                    if (!newValue) field.onChange("")
                                    else
                                        field.onChange(
                                            dayJsToStrForDnoApi(newValue)
                                        )
                                } else {
                                    setError(registerName, {
                                        type: "manual",
                                        message: "Invalid Date",
                                    })
                                }
                            }}
                            label={label}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    ...TEXT_FIELD_DEFAULT_PROPS,
                                    error: !!error,
                                    helperText: error?.message,
                                },
                            }}
                            maxDate={
                                disableFutureDates
                                    ? dayjs(new Date())
                                    : undefined
                            }
                        />
                    )}
                />
                {tooltip && !isMob && <HelpIcon tooltip={tooltip} />}
            </Stack>
        </Box>
    )
}

ValidatedDatePicker.defaultProps = {
    required: false,
    tooltip: null,
    disabled: false,
    disableFutureDates: false,
}

export default ValidatedDatePicker
